'use client';

import { faCheckCircle, faHand } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import { GridStack, type GridStackOptions, type GridStackWidget } from 'gridstack';
import 'gridstack/dist/gridstack.css';
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Highlighter from '~/components/ui/AnimatedComponents/Highlighter';
import { Button } from '../..';

const solutionHighlights = [
    {
        title: 'Entlastung im Alltag',
        text: 'Wiederkehrende Fragen, neue Inhalte und spontane Aenderungen werden schneller erledigt.'
    },
    {
        title: 'Website ohne Umwege pflegen',
        text: 'Texte, Seiten und Infos lassen sich direkt im CMS aktualisieren, ohne technische Huerden.'
    },
    {
        title: 'KI dort, wo sie Zeit spart',
        text: 'KI unterstuetzt bei Antworten, Texten und Prozessen, damit dein Team sich auf Mitglieder und Sport konzentriert.'
    }
];

function createInitialWidgets(): GridStackWidget[] {
    return [
        {
            id: 'showcase-hero',
            x: 0,
            y: 0,
            w: 5,
            h: 4,
            minW: 5,
            minH: 5
        },
        {
            id: 'showcase-inbox',
            x: 5,
            y: 0,
            w: 3,
            h: 4,
            minW: 3,
            minH: 6
        },
        {
            id: 'showcase-news',
            x: 0,
            y: 4,
            w: 3,
            h: 3,
            minW: 4,
            minH: 5
        },
        {
            id: 'showcase-approvals',
            x: 4,
            y: 6,
            w: 3,
            h: 3,
            minW: 4,
            minH: 5
        }
    ];
}

/**
 * The demo's own grid options.
 *
 * They used to be spread from the CMS module's `CMS_BASE_GRID_OPTIONS`; that module
 * is gone with the gridstack editor. This section is marketing — an animated
 * illustration on the public homepage — so it keeps its own copy rather than the
 * new CMS growing a dependency for a landing-page animation.
 */
function createInitialOptions(): GridStackOptions {
    return {
        columnOpts: {
            breakpointForWindow: true,
            breakpoints: [
                { c: 1, w: 700 },
                { c: 3, w: 850 },
                { c: 6, w: 950 },
                { c: 8, w: 1100 }
            ],
            layout: 'moveScale',
            columnMax: 8
        },
        marginTop: 8,
        acceptWidgets: false,
        float: true,
        staticGrid: false,
        disableDrag: false,
        disableResize: false,
        margin: 8,
        cellHeight: 58,
        minRow: 7
    };
}

export function AiCmsShowcaseSection() {
    const [gridKey, setGridKey] = useState(0);
    const initialOptions = useMemo(() => createInitialOptions(), []);
    const initialWidgets = useMemo(() => createInitialWidgets(), []);

    const handleReset = () => {
        setGridKey((value) => value + 1);
    };

    return (
        <section className="tw-grid-feature" id="ai">
            <h2 className="tw-mb-4 tw-font-semibold">
                <Highlighter strokeWidth={2} iterations={2} action="highlight" variant="successLight">
                    Neue KI Funktionen und das CMS für deinen Sportverein
                </Highlighter>
            </h2>

            <p className="tw-mb-8 tw-text-accents-8">
                Spare Zeit, reduziere Rückfragen und pflege deine Sportverein Website deutlich schneller. Mit dem neuen
                CMS und vielen neuen KI Funktionen wird Vereinsarbeit einfacher, moderner und effizienter.
            </p>

            <div className="tw-grid tw-gap-6 lg:tw-grid-cols-2">
                {/* Linke Spalte: Problem / Lösung */}
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.4 }}
                    className="tw-relative tw-flex tw-flex-col tw-overflow-hidden tw-rounded-[2rem] tw-border tw-border-solid tw-border-accents-2 tw-bg-gradient-to-br tw-from-white tw-via-successLight/30 tw-to-success/10 tw-p-6 md:tw-p-8"
                >
                    <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-[radial-gradient(circle_at_top_right,_rgba(34,197,94,0.18),_transparent_42%)]" />
                    <div className="tw-relative tw-z-10">
                        <span className="tw-inline-flex tw-rounded-full tw-border tw-border-success/20 tw-bg-white/80 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-success">
                            KI Funktionen
                        </span>
                        <h3 className="tw-mb-3 tw-mt-4 tw-max-w-xl tw-text-2xl tw-font-semibold tw-leading-tight md:tw-text-4xl">
                            Weniger Verwaltungsaufwand, weniger Rückfragen
                        </h3>
                        <p className="tw-mb-0 tw-max-w-2xl tw-text-lg tw-leading-relaxed tw-text-accents-7">
                            Viele Sportvereine verlieren Zeit durch manuelle Inhaltspflege, wiederkehrende Fragen und
                            veraltete Webseiten. Sportision löst genau dieses Problem mit einem Club-CMS für schnelle
                            Inhaltsupdates und KI Funktionen, die Anfragen, Texte und Prozesse intelligent
                            unterstützen.
                        </p>
                    </div>

                    <div className="tw-relative tw-z-10 tw-mt-8">
                        <ul className="tw-flex tw-flex-col tw-gap-3 tw-p-0">
                            {solutionHighlights.map((item, index) => (
                                <motion.li
                                    key={item.title}
                                    initial={{ opacity: 0, y: 14 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.4 }}
                                    transition={{ duration: 0.35, delay: index * 0.08 }}
                                    className="tw-flex tw-items-start tw-gap-3 tw-rounded-2xl tw-bg-white/85 tw-p-4 tw-shadow-lg tw-backdrop-blur"
                                >
                                    <FontAwesomeIcon icon={faCheckCircle} className="tw-mt-1 tw-text-success" />
                                    <span className="tw-text-sm tw-leading-6 tw-text-accents-7">
                                        <strong className="tw-text-accents-8">{item.title}:</strong> {item.text}
                                    </span>
                                </motion.li>
                            ))}
                        </ul>
                    </div>
                    <Button.Link
                        className="tw-mt-auto"
                        href="https://calendly.com/sportision-team/beratung-zur-digitalisierung-mit-sportision"
                    >
                        Jetzt beraten lassen
                    </Button.Link>
                </motion.div>

                {/* Rechte Spalte: GridStack Demo */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.985 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.45 }}
                    className="tw-rounded-3xl tw-border tw-border-solid tw-border-accents-2 tw-bg-accents-0 tw-p-5 tw-text-accents-8"
                >
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                        <div>
                            <p className="tw-text-xs tw-uppercase tw-tracking-widest tw-text-accents-7">CMS Demo</p>
                            <h3 className="tw-mt-2 tw-text-2xl tw-font-semibold tw-text-accents-8">
                                Die neue Website einfach selbst verwalten
                            </h3>
                        </div>
                        <Button
                            className="tw-shrink-0"
                            flat
                            size="xs"
                            onClick={handleReset}
                            aria-label="Layout zurücksetzen"
                        >
                            Layout zurücksetzen
                        </Button>
                    </div>

                    <div className="showcase-gridstack tw-relative tw-h-[810px] tw-overflow-hidden tw-rounded-2xl tw-border tw-border-success/20">
                        <ShowcaseGridCanvas key={gridKey} widgets={initialWidgets} options={initialOptions} />
                    </div>
                </motion.div>
            </div>

            <style jsx global>{`
                .showcase-gridstack .grid-stack {
                    height: 100%;
                    min-height: 100%;
                    background: #ffffff;
                }

                /* Drag-Handle deutlicher machen */
                .showcase-gridstack .grid-stack-item .grid-stack-item-content {
                    cursor: grab;
                    transition:
                        box-shadow 0.2s ease,
                        border-color 0.2s ease;
                }
                .showcase-gridstack .grid-stack-item.ui-draggable-dragging .grid-stack-item-content {
                    cursor: grabbing;
                    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08);
                    border-color: rgba(56, 193, 114, 0.5);
                }

                .showcase-gridstack .grid-stack-item {
                    padding: 6px;
                }

                .showcase-gridstack .grid-stack-item-content {
                    inset: 6px !important;
                    overflow: hidden;
                    border-radius: 22px;
                    background: transparent;
                    border: 1px solid rgba(56, 193, 114, 0.32);
                    box-shadow: none;
                }

                .showcase-gridstack .grid-stack-item-content > * {
                    height: 100%;
                }

                .showcase-gridstack .ui-resizable-se {
                    width: 18px;
                    height: 18px;
                    right: 13px;
                    bottom: 13px;
                    background-repeat: no-repeat;
                    background-position: center;
                    opacity: 0.6;
                    transition: opacity 0.2s;
                }
                .showcase-gridstack .ui-resizable-se:hover {
                    opacity: 1;
                }

                /* Platzhalter mit sanfter Animation */
                .showcase-gridstack .grid-stack-placeholder > .placeholder-content {
                    background: rgba(56, 193, 114, 0.08);
                    border: 1.5px dashed rgba(56, 193, 114, 0.6);
                    border-radius: 22px;
                    transition: all 0.2s ease;
                }

                .gs-8 {
                    background: transparent !important;
                }

                /* Responsive: Auf kleinen Bildschirmen das Grid deaktivieren (statische Vorschau) */
                @media (max-width: 768px) {
                    .showcase-gridstack .grid-stack-item {
                        cursor: default !important;
                    }
                    .showcase-gridstack .grid-stack-item .ui-resizable-handle {
                        display: none !important;
                    }
                    .showcase-gridstack .grid-stack-item.ui-draggable-dragging .grid-stack-item-content {
                        cursor: default;
                        box-shadow: none;
                    }
                }
            `}</style>
        </section>
    );
}

interface ShowcaseGridCanvasProps {
    widgets: GridStackWidget[];
    options: GridStackOptions;
}

function ShowcaseGridCanvas({ widgets, options }: ShowcaseGridCanvasProps) {
    const containerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const element = containerRef.current;
        if (!element) return;

        const grid = GridStack.init(options, element);

        return () => {
            grid.destroy(false);
        };
    }, [options]);

    return (
        <div ref={containerRef} className="grid-stack">
            {widgets.map((widget) => (
                <div
                    key={widget.id}
                    className="grid-stack-item"
                    gs-id={widget.id}
                    gs-x={widget.x}
                    gs-y={widget.y}
                    gs-w={widget.w}
                    gs-h={widget.h}
                    gs-min-w={widget.minW}
                    gs-min-h={widget.minH}
                >
                    <div className="grid-stack-item-content">
                        {widget.id === 'showcase-hero' ? <ShowcaseHeroWidget /> : null}
                        {widget.id === 'showcase-inbox' ? <ShowcaseInboxWidget /> : null}
                        {widget.id === 'showcase-news' ? <ShowcaseNewsWidget /> : null}
                        {widget.id === 'showcase-approvals' ? <ShowcaseApprovalsWidget /> : null}
                    </div>
                </div>
            ))}
        </div>
    );
}

function ShowcaseWidgetShell({
    title,
    tag,
    children,
    footer
}: {
    title: string;
    tag: string;
    children: ReactNode;
    footer?: ReactNode;
}) {
    return (
        <div className="tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-border tw-border-success/20 tw-bg-white">
            <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-success/20 tw-bg-white tw-px-3 tw-py-2">
                <div className="tw-flex tw-w-full tw-items-start tw-justify-between tw-pt-2">
                    <div>
                        <p className="tw-mb-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-widest tw-text-success">
                            {tag}
                        </p>
                        <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-accents-9">{title}</p>
                    </div>

                    <span
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-success/20 tw-bg-success/5 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-uppercase tw-text-success"
                        title="Zum Verschieben ziehen"
                    >
                        <FontAwesomeIcon icon={faHand} /> drag
                    </span>
                </div>
            </div>
            <div className="tw-flex-1 tw-px-3 tw-py-2">{children}</div>
            {footer && <div className="tw-border-t tw-border-success/20 tw-bg-white tw-px-3 tw-py-2">{footer}</div>}
        </div>
    );
}

function ShowcaseHeroWidget() {
    return (
        <ShowcaseWidgetShell title="Neue Vereinsseite" tag="Content">
            <div className="tw-flex tw-h-full tw-flex-col tw-justify-between">
                <div>
                    <p className="tw-mt-2 tw-max-w-sm tw-text-sm tw-leading-6 tw-text-accents-8">
                        Erstelle neue Seiten für deinen Verein oder Sportschule, passe Inhalte in wenigen Minuten an und
                        halte deine Website immer aktuell.
                    </p>
                </div>
                <div className="tw-mt-3 tw-grid tw-grid-cols-2 tw-gap-2">
                    <div className="tw-rounded-xl tw-border tw-border-accents-3 tw-bg-white tw-p-2">
                        <p className="tw-m-0 tw-text-sm tw-text-accents-7">Neue Inhalte</p>
                        <p className="tw-m-0 tw-text-base tw-font-semibold tw-text-accents-8">Schnell online</p>
                    </div>
                    <div className="tw-rounded-xl tw-border tw-border-accents-3 tw-bg-white tw-p-2">
                        <p className="tw-m-0 tw-text-sm tw-text-accents-7">Status</p>
                        <p className="tw-m-0 tw-text-base tw-font-semibold tw-text-accents-8">Live</p>
                    </div>
                </div>
            </div>
        </ShowcaseWidgetShell>
    );
}

function ShowcaseInboxWidget() {
    return (
        <ShowcaseWidgetShell title="Digitaler Club Assistent" tag="Automation">
            <div className="tw-space-y-1">
                {[
                    'Eingehende Anfragen werden auf der Website teilautomatisiert beantwortet.',
                    'Mitgliederfragen schneller geklärt',
                    'Texte und Antworten für dein Team vorbereitet'
                ].map((item, index) => (
                    <div
                        key={item}
                        className="tw-flex tw-items-start tw-gap-2 tw-rounded-xl tw-border tw-border-accents-3 tw-bg-white tw-p-2"
                    >
                        <span className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-accents-3 tw-bg-white tw-text-sm tw-font-semibold tw-text-accents-8">
                            0{index + 1}
                        </span>
                        <p className="tw-mb-0 tw-text-sm tw-leading-6 tw-text-accents-8">{item}</p>
                    </div>
                ))}
            </div>
        </ShowcaseWidgetShell>
    );
}

function ShowcaseNewsWidget() {
    return (
        <ShowcaseWidgetShell title="KI Tools" tag="Produktivität">
            <div>
                <div className="tw-rounded-xl tw-border tw-border-accents-3 tw-bg-white tw-p-2">
                    <p className="tw-m-0 tw-text-sm tw-text-accents-7">KI Formular Builder</p>
                    <p className="tw-mt-1 tw-text-sm tw-font-medium tw-text-accents-8">Formulare schneller erstellen</p>
                </div>
                <div className="tw-rounded-xl tw-border tw-border-accents-3 tw-bg-white tw-p-2">
                    <p className="tw-m-0 tw-text-sm tw-text-accents-7">KI Text & Regeln</p>
                    <p className="tw-mt-1 tw-text-sm tw-font-medium tw-text-accents-8">
                        Texte schreiben und Regeln einfacher bauen
                    </p>
                </div>
            </div>
        </ShowcaseWidgetShell>
    );
}

function ShowcaseApprovalsWidget() {
    return (
        <ShowcaseWidgetShell title="Zeit sparen im Vereinsalltag" tag="Vorteile">
            <div className="tw-flex tw-h-full tw-flex-col tw-justify-between">
                <div className="tw-space-y-2">
                    <div>
                        <p className="tw-mb-0 tw-text-sm tw-text-accents-7">Mehr Effizienz</p>
                        <p className="tw-mt-1 tw-text-sm tw-font-medium tw-text-accents-8">
                            Weniger Aufwand für Website und Anfragen
                        </p>
                    </div>
                    <div className="tw-relative tw-h-5 tw-rounded-full tw-bg-accents-1">
                        <div className="tw-h-5 tw-w-2/3 tw-rounded-full tw-bg-success" />
                        <p className="tw-absolute tw-left-1/2 tw-top-1/2 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-transform tw-font-bold tw-text-white">
                            66%
                        </p>
                    </div>
                </div>
                <div className="tw-mt-3 tw-grid tw-grid-cols-2 tw-gap-2">
                    <div className="tw-rounded-xl tw-border tw-border-accents-3 tw-bg-white tw-p-2">
                        <p className="tw-m-0 tw-text-sm tw-text-accents-7">Weniger Rückfragen</p>
                        <p className="tw-mt-1 tw-text-sm tw-font-medium tw-text-accents-8">Mehr Entlastung</p>
                    </div>
                    <div className="tw-rounded-xl tw-border tw-border-accents-3 tw-bg-white tw-p-2">
                        <p className="tw-m-0 tw-text-sm tw-text-accents-7">Schnellere Pflege</p>
                        <p className="tw-mt-1 tw-text-sm tw-font-medium tw-text-accents-8">
                            Passt zu deinem Verein oder Sportschule
                        </p>
                    </div>
                </div>
            </div>
        </ShowcaseWidgetShell>
    );
}
